import * as c from 'shared/constants'
import { constructAxiosGet, constructAxiosPost, constructAxiosDelete } from 'shared/utilities/Axios'
import { requestWithAuth } from 'shared/utilities/authUtils'
import { setTotalPropertyResults } from 'Store/DashboardState/actions'
import { setCustomSearchResults } from 'Store/AdvancedSearch/actions'
import {
  clearAdvancedSearchAbortController,
  createAdvancedSearchAbortSignal,
} from 'Store/Request/advancedSearchAbort'

// Cache helpers for district dashboard responses
const CACHE_PREFIX = 'dap_cache_'
const CACHE_INVALIDATION_HOUR = 7 // 7am Eastern

const isCacheValid = () => {
  const now = new Date()
  const cacheDate = localStorage.getItem(CACHE_PREFIX + 'date')
  if (!cacheDate) return false
  const cached = new Date(cacheDate)
  // Invalid if cached before today's 7am
  const today7am = new Date(now.getFullYear(), now.getMonth(), now.getDate(), CACHE_INVALIDATION_HOUR, 0, 0)
  if (now >= today7am && cached < today7am) return false
  // Invalid if cached before yesterday's 7am and it's before today's 7am
  if (now < today7am) {
    const yesterday7am = new Date(today7am)
    yesterday7am.setDate(yesterday7am.getDate() - 1)
    if (cached < yesterday7am) return false
  }
  return true
}

const getCachedResponse = (cacheKey) => {
  if (!isCacheValid()) return null
  try {
    const cached = localStorage.getItem(CACHE_PREFIX + cacheKey)
    return cached ? JSON.parse(cached) : null
  } catch {
    return null
  }
}

const setCachedResponse = (cacheKey, data) => {
  try {
    localStorage.setItem(CACHE_PREFIX + cacheKey, JSON.stringify(data))
    localStorage.setItem(CACHE_PREFIX + 'date', new Date().toISOString())
  } catch {
    // localStorage full or unavailable — silently skip
  }
}

export const addRequest = requestConstant => ({
  type: c.ADD_REQUEST,
  requestConstant,
})

export const handleRequestResults = (response, requestConstant, dispatch) => {
  if (requestConstant === c.GEOGRAPHY_HOUSING_TYPE_ALL) {
    dispatch(setTotalPropertyResults(response.data))
  } else if (requestConstant === c.ADVANCED_SEARCH) {
    dispatch(setCustomSearchResults(response.data))
  }
  return { type: c.HANDLE_REQUEST_RESULTS, requestConstant: requestConstant, results: response.data }
}

export const removeRequest = requestConstant => ({
  type: c.REMOVE_REQUEST,
  requestConstant,
})

export const removeManyRequests = requestConstantsArray => ({
  type: c.REMOVE_MANY_REQUESTS,
  requestConstantsArray,
})

export const retryAuthenticatedRequests = () => (dispatch, getState) => {
  const appState = getState().appState
  if (!appState) return

  appState.requests.forEach(request => {
    if (!request.isAuthenticated) return
    // Keep custom search filters after login; user can submit manually.
    if (request.type === c.ADVANCED_SEARCH) return
    request.called = false
    dispatch(requestWithAuth(makeRequest(request)))
  })
}

export const makeRequest = dataRequest => (dispatch, getState, access_token) => {
  if (!dataRequest || dataRequest.called) return

  dispatch(addRequest(dataRequest.requestConstant))
  dataRequest.called = true

  // Check localStorage cache for district dashboard requests
  if (dataRequest.requestConstant === c.GEOGRAPHY_HOUSING_TYPE_ALL) {
    const cacheKey = dataRequest.path + JSON.stringify(dataRequest.params || {})
    const cached = getCachedResponse(cacheKey)
    if (cached) {
      dispatch(handleRequestResults({ data: cached }, dataRequest.requestConstant, dispatch))
      return Promise.resolve()
    }
  }

  const requestId = Math.floor(Math.random() * 1000000)
  const requestOptions =
    dataRequest.requestConstant === c.ADVANCED_SEARCH
      ? { signal: createAdvancedSearchAbortSignal() }
      : {}

  return constructAxiosGet(
    dispatch,
    getState,
    requestId,
    dataRequest.path,
    dataRequest.params,
    access_token,
    dataRequest.requestConstant,
    (response, requestConstant, dispatch) => {
      if (requestConstant === c.ADVANCED_SEARCH) {
        clearAdvancedSearchAbortController()
      }
      // Cache district dashboard responses
      if (requestConstant === c.GEOGRAPHY_HOUSING_TYPE_ALL) {
        const cacheKey = dataRequest.path + JSON.stringify(dataRequest.params || {})
        setCachedResponse(cacheKey, response.data)
      }
      return handleRequestResults(response, requestConstant, dispatch)
    },
    null,
    requestOptions
  )
}

export const makeBblCsvrequest = (dataRequest, bblList) => (dispatch, getState, access_token) => {
  dispatch(addRequest(dataRequest.csvRequestConstant))
  const requestId = Math.floor(Math.random() * 1000000)

  return constructAxiosPost(
    dispatch,
    getState,
    requestId,
    '/bbls',
    JSON.stringify(bblList), // Body
    dataRequest.csv_params,
    access_token,
    dataRequest.csvRequestConstant,
    undefined
  )
}

export const makeCsvRequest = dataRequest => (dispatch, getState, access_token) => {
  dispatch(addRequest(dataRequest.csvRequestConstant))
  const requestId = Math.floor(Math.random() * 1000000)
  return constructAxiosGet(
    dispatch,
    getState,
    requestId,
    dataRequest.path,
    dataRequest.csv_params,
    access_token,
    dataRequest.csvRequestConstant,
    undefined
  )
}

export const postUserRequest = (userRequestData, hideModal) => (dispatch, getState, access_token) => {
  const requestId = Math.floor(Math.random() * 1000000)
  return constructAxiosPost(
    dispatch,
    getState,
    requestId,
    '/user-requests/',
    userRequestData, // Body
    { format: null },
    access_token,
    'POST_USER_REQUEST',
    undefined,
    hideModal
  )
}

export const postUserAccessRequest = (userAccessRequestData, hideModal) => (dispatch, getState, access_token) => {
  const requestId = Math.floor(Math.random() * 1000000)
  return constructAxiosPost(
    dispatch,
    getState,
    requestId,
    'users/access-request/',
    userAccessRequestData, // Body
    { format: null },
    access_token,
    'POST_USER_ACCESS_REQUEST',
    undefined,
    hideModal
  )
}

export const deleteUserAccessRequest = (postDelete) => (dispatch, getState, access_token) => {
  const requestId = Math.floor(Math.random() * 1000000)
  return constructAxiosDelete(
    dispatch,
    getState,
    requestId,
    'users/access-request/',
    null, // Body
    { format: null },
    access_token,
    'DELETE_USER_ACCESS_REQUEST',
    undefined,
    postDelete
  )
}

export const postUserRegister = (userRegisterData, hideModal) => (dispatch, getState, access_token) => {
  const requestId = Math.floor(Math.random() * 1000000)
  return constructAxiosPost(
    dispatch,
    getState,
    requestId,
    '/users/register/',
    userRegisterData, // Body
    { format: null },
    access_token,
    'POST_USER_REGISTER',
    undefined,
    hideModal
  )
}

export const postUserMessage = (bugData, hideModal) => (dispatch, getState, access_token) => {
  const requestId = Math.floor(Math.random() * 1000000)
  return constructAxiosPost(
    dispatch,
    getState,
    requestId,
    '/user-messages/',
    JSON.stringify(bugData), // Body
    { format: null },
    access_token,
    'POST_BUG_REPORT',
    undefined,
    hideModal
  )
}
